import { format } from "date-fns";
import { downloadCSV } from "react-admin";
import jsonExport from "jsonexport/dist";

// Helper to format timestamp to date string
const formatDate = (timestamp) => {
  if (!timestamp) return "";
  try {
    return format(new Date(timestamp), "MM/dd/yyyy");
  } catch {
    return "";
  }
};

// Helper to safely get array as string
const arrayToString = (arr) => {
  if (!arr || !Array.isArray(arr)) return "";
  return arr.join(", ");
};

// Helper to get first item from array for flattening
const getFirst = (arr, field, isDate = false) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return "";
  const value = arr[0]?.[field] ?? "";
  return isDate && value ? formatDate(value) : value;
};

const providerExporter = (providers) => {
  const dataForExport = providers.map((p, index) => ({
    index: index + 1,
    id: p.id || "",
    firstName: p.firstName || "",
    lastName: p.lastName || "",
    email: p.email || "",
    phoneNumber: p.phoneNumber || "",
    alias: p.alias || "",
    shortUserId: p.shortUserId || "",
    userRole: p.userRole || "",
    isActive: p.isActive ?? "",
    "geoLocation.lat": p.geoLocation?.lat ?? "",
    "geoLocation.lng": p.geoLocation?.lng ?? "",
    funFacts: p.funFacts || "",
    "about.childcare": p.about?.childcare || "",
    "about.childcarePlus": p.about?.childcarePlus || "",
    "about.cleaning": p.about?.cleaning || "",
    "about.cooking": p.about?.cooking || "",
    "about.virtual": p.about?.virtual || "",
    "about.night_nurse": p.about?.night_nurse || "",
    "about.nightNurse": p.about?.nightNurse || "",
    "about.housekeeping": p.about?.housekeeping || "",
    experienceSince: formatDate(p.experienceSince),
    "address.streetAddress": p.address?.streetAddress || "",
    "address.unit": p.address?.unit || "",
    "address.city": p.address?.city || "",
    "address.state": p.address?.state || "",
    "address.zipcode": p.address?.zipcode || "",
    allowsDogs: p.allowsDogs ?? "",
    allowsCats: p.allowsCats ?? "",
    hasCar: p.hasCar ?? "",
    hasPublicProfile: p.hasPublicProfile ?? "",
    services: arrayToString(p.services),
    supportedPaymentMethods: arrayToString(p.supportedPaymentMethods),
    "childcareAgeRange.min": p.childcareAgeRange?.min ?? "",
    "childcareAgeRange.max": p.childcareAgeRange?.max ?? "",
    hasSpecialNeedsExperience: p.hasSpecialNeedsExperience ?? "",
    isSubscriber: p.isSubscriber ?? "",
    desirableCommutingTime: p.desirableCommutingTime || "",
    becomingProviderLastStep: p.becomingProviderLastStep || "",
    operationRadius: p.operationRadius
      ? (p.operationRadius / 1609.34).toFixed(1)
      : "",
    created: formatDate(p.created),
    speaksLanguages: arrayToString(p.speaksLanguages),
    specialNeeds: p.specialNeeds || "",
    profilePic: p.profilePic || "",
    "certificates.name": getFirst(p.certificates, "name"),
    "certificates.timestamp": getFirst(p.certificates, "timestamp", true),
    numberOfReferences: p.numberOfReferences ?? "",
    identificationPhoto: p.identificationPhoto || "",
    "references.email": getFirst(p.references, "email"),
    "references.name": getFirst(p.references, "name"),
    "references.phoneNumber": getFirst(p.references, "phoneNumber"),
    zellePhoneNumber: p.zellePhoneNumber || "",
    resume: p.resume || "",
    CPRCertificate: p.CPRCertificate || "",
    zelleEmail: p.zelleEmail || "",
    gender: p.gender || "",
    dateOfBirthTimestamp: formatDate(p.dateOfBirthTimestamp),
    "covidCommitments.action": getFirst(p.covidCommitments, "action"),
    "covidCommitments.date": getFirst(p.covidCommitments, "date", true),
    "emergencyContacts.name": getFirst(p.emergencyContacts, "name"),
    "emergencyContacts.phoneNumber": getFirst(
      p.emergencyContacts,
      "phoneNumber",
    ),
    "emergencyContacts.relationship": getFirst(
      p.emergencyContacts,
      "relationship",
    ),
    "blurbs.childcare": p.blurbs?.childcare || "",
    "blurbs.childcarePlus": p.blurbs?.childcarePlus || "",
    "blurbs.cleaning": p.blurbs?.cleaning || "",
    "blurbs.cooking": p.blurbs?.cooking || "",
    "blurbs.virtual": p.blurbs?.virtual || "",
    "blurbs.night_nurse": p.blurbs?.night_nurse || "",
    "blurbs.nightNurse": p.blurbs?.nightNurse || "",
    "blurbs.housekeeping": p.blurbs?.housekeeping || "",
    lastBackgroundCheck: formatDate(p.lastBackgroundCheck),
    allowsSmoking: p.allowsSmoking ?? "",
    enrichmentActivities: arrayToString(p.enrichmentActivities),
    "regularSchedule.weekday": getFirst(p.regularSchedule, "weekday"),
    "regularSchedule.allDay": getFirst(p.regularSchedule, "allDay"),
    "regularSchedule.startTime": getFirst(p.regularSchedule, "startTime"),
    "regularSchedule.endTime": getFirst(p.regularSchedule, "endTime"),
    reliabilityScore: p.reliabilityScore ?? "",
    adminNotes: p.adminNotes || "",
    acceptedProviderTerms: p.acceptedProviderTerms || "",
    "prices.childcare": p.prices?.childcare ?? "",
    "prices.childcarePlus": p.prices?.childcarePlus ?? "",
    "prices.cleaning": p.prices?.cleaning ?? "",
    "prices.cooking": p.prices?.cooking ?? "",
    "prices.virtual": p.prices?.virtual ?? "",
    "prices.night_nurse": p.prices?.night_nurse ?? "",
    "prices.housekeeping": p.prices?.housekeeping ?? "",
    extraService: p.extraService || "",
  }));

  jsonExport(
    dataForExport,
    { headers: Object.keys(dataForExport[0] || {}) },
    (err, csv) => {
      downloadCSV(csv, "providers");
    },
  );
};

export default providerExporter;
